import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import {
  getThreads,
  createThread,
  getMessages,
  sendMessage,
  editMessage,
  deleteMessage,
  markThreadRead,
  deleteThread,
  toggleThreadLock,
} from '../services/messageService';
import {
  getTasks, createTask, updateTask, deleteTask,
  addSubtask, updateSubtask, deleteSubtask,
  addTaskAttachment, removeTaskAttachment,
} from '../services/taskService';
import { getProjects } from '../services/projectService';
import { getSupplyRequests } from '../services/supplyRequestService';
import { lookupWrikeFolder } from '../services/wrikeService';
import api from '../services/api';
import { uploadFile, openFile, downloadFile } from '../services/fileService';
import MessageCompose from '../components/MessageCompose';
import './SpacePage.css';

const STATUS_COLS = ['todo', 'in-progress', 'done'];
const STATUS_LABELS = { todo: 'To Do', 'in-progress': 'In Progress', done: 'Done' };
const PRIORITY_COLORS = { low: '#6db33f', medium: '#e8a020', high: '#d94040' };

const buildWrikeFolderHref = (value) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (/^https?:\/\//i.test(text)) return text;
  if (/^\d+$/.test(text)) return `https://app-us2.wrike.com/open.htm?id=${encodeURIComponent(text)}`;
  return `https://app-us2.wrike.com/workspace.htm#/folder/${encodeURIComponent(text)}`;
};

const getWrikeFolderValue = (space) =>
  String(space?.settings?.wrikeFolderId || space?.settings?.wrikeFolderUrl || '').trim();

export default function SpacePage() {
  const { spaceId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { selectSpace, activeSpace, fetchSpaces, upsertSpace, spaces } = useSpaces();
  const VALID_TABS = ['threads', 'tasks', 'supply-requests', 'members', 'info'];
  const initialTab = VALID_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'threads';
  const [tab, setTab] = useState(initialTab);

  // ── Threads ──────────────────────────────────────────────────────────────────
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newSubject, setNewSubject] = useState('');
  const [newThreadPrivate, setNewThreadPrivate] = useState(false);
  const [showNewThread, setShowNewThread] = useState(false);
  const [threadsLoading, setThreadsLoading] = useState(true);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editBody, setEditBody] = useState('');

  // ── Tasks ─────────────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskPriority, setNewTaskPriority] = useState('medium');
  const [showNewTask, setShowNewTask] = useState(false);
  const [activeTask, setActiveTask] = useState(null); // task detail panel
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [editingTaskTitle, setEditingTaskTitle] = useState(false);
  const [editTaskTitleVal, setEditTaskTitleVal] = useState('');
  const [editingTaskDesc, setEditingTaskDesc] = useState(false);
  const [editTaskDescVal, setEditTaskDescVal] = useState('');
  const [spaceFiles, setSpaceFiles] = useState([]);
  const [showSpaceFilePicker, setShowSpaceFilePicker] = useState(false);
  const [projects, setProjects] = useState([]);
  const [supplyRequests, setSupplyRequests] = useState([]);
  const [supplyRequestsLoading, setSupplyRequestsLoading] = useState(false);
  const [supplyRequestsError, setSupplyRequestsError] = useState('');

  // ── Members / mentions ─────────────────────────────────────────────────────────
  const [spaceDetails, setSpaceDetails] = useState(null);
  const [spaceMembers, setSpaceMembers] = useState([]); // [{_id, name}] for @mentions
  const [allUsers, setAllUsers] = useState([]);
  const [addMemberId, setAddMemberId] = useState('');
  const [addMemberRole, setAddMemberRole] = useState('member');
  const [copiedField, setCopiedField] = useState(null); // 'slug' | 'id'

  // ── Space Info (editing) ───────────────────────────────────────────────────────
  const [editSpaceName, setEditSpaceName] = useState('');
  const [editSpaceDescription, setEditSpaceDescription] = useState('');
  const [editOrganizationKey, setEditOrganizationKey] = useState('');
  const [editTeamKey, setEditTeamKey] = useState('');
  const [editParentSpaceId, setEditParentSpaceId] = useState('');
  const [editGovernanceClassification, setEditGovernanceClassification] = useState('internal');
  const [editGovernanceOwnerType, setEditGovernanceOwnerType] = useState('space');
  const [editGovernanceOwnerName, setEditGovernanceOwnerName] = useState('');
  const [editRequireAdminApproval, setEditRequireAdminApproval] = useState(false);
  const [editAllowMemberUploads, setEditAllowMemberUploads] = useState(true);
  const [editAllowExternalSharing, setEditAllowExternalSharing] = useState(false);
  const [editAllowDuplicateContainers, setEditAllowDuplicateContainers] = useState(true);
  const [editConfidentialAdminIds, setEditConfidentialAdminIds] = useState([]);
  const [editAllowSupplyRequestWrikePush, setEditAllowSupplyRequestWrikePush] = useState(false);
  const [editWrikeFolderUrl, setEditWrikeFolderUrl] = useState('');
  const [wrikeLookupLoading, setWrikeLookupLoading] = useState(false);
  const [wrikeLookupError, setWrikeLookupError] = useState('');
  const [wrikeLookupResolvedId, setWrikeLookupResolvedId] = useState('');
  const [wrikeLookupMatches, setWrikeLookupMatches] = useState([]);
  const [isEditingSpaceInfo, setIsEditingSpaceInfo] = useState(false);
  const [updatingSpaceInfo, setUpdatingSpaceInfo] = useState(false);
  const [spaceInfoError, setSpaceInfoError] = useState('');

  const syncSpaceState = useCallback((nextSpace) => {
    setSpaceDetails(nextSpace);
    setSpaceMembers(
      (nextSpace?.members || []).filter((m) => m.user).map((m) => ({
        _id: String(m.user._id || m.user),
        name: m.user.name || '',
      }))
    );
    // Initialize edit fields from fetched data
    setEditSpaceName(nextSpace?.name || '');
    setEditSpaceDescription(nextSpace?.description || '');
    setEditOrganizationKey(nextSpace?.organizationKey || '');
    setEditTeamKey(nextSpace?.teamKey || '');
    setEditParentSpaceId(String(nextSpace?.parentSpaceId?._id || nextSpace?.parentSpaceId || ''));
    setEditGovernanceClassification(nextSpace?.governance?.classification || 'internal');
    setEditGovernanceOwnerType(nextSpace?.governance?.dataOwnerType || 'space');
    setEditGovernanceOwnerName(nextSpace?.governance?.dataOwnerName || '');
    setEditRequireAdminApproval(Boolean(nextSpace?.governance?.requireAdminApprovalForMembership));
    setEditAllowMemberUploads(nextSpace?.governance?.allowMemberUploads !== false);
    setEditAllowExternalSharing(Boolean(nextSpace?.governance?.allowExternalSharing));
    setEditAllowDuplicateContainers(nextSpace?.governance?.allowDuplicateContainers !== false);
    setEditConfidentialAdminIds((nextSpace?.governance?.confidentialAdminIds || []).map((entry) => String(entry?._id || entry)));
    setEditAllowSupplyRequestWrikePush(Boolean(nextSpace?.settings?.allowSupplyRequestWrikePush));
    setEditWrikeFolderUrl(nextSpace?.settings?.wrikeFolderId || nextSpace?.settings?.wrikeFolderUrl || '');
  }, []);

  // Reset state when switching space; also eagerly loads members for @mentions
  useEffect(() => {
    setActiveThread(null);
    setMessages([]);
    setTab('threads');
    setShowNewThread(false);
    setSpaceDetails(null);
    setSpaceMembers([]);
    setAllUsers([]);
    selectSpace(spaceId).catch((err) => console.error('selectSpace failed:', err));
    setThreadsLoading(true);
    Promise.all([getThreads(spaceId), api.get(`/spaces/${spaceId}`)])
      .then(([threadsRes, spaceRes]) => {
        setThreads(threadsRes.data.threads || []);
        const sd = spaceRes.data.space;
        syncSpaceState(sd);
      })
      .catch((err) => console.error('Space/threads load failed:', err))
      .finally(() => setThreadsLoading(false));
  }, [spaceId, syncSpaceState, selectSpace]);

  // Load tasks + projects when tasks tab is opened
  useEffect(() => {
    if (tab !== 'tasks') return;
    setTasksLoading(true);
    Promise.all([getTasks(spaceId), getProjects()])
      .then(([tasksRes, projectsRes]) => {
        setTasks(tasksRes.data.tasks || []);
        setProjects(projectsRes.data.projects || []);
      })
      .catch((err) => console.error('getTasks failed:', err))
      .finally(() => setTasksLoading(false));
  }, [tab, spaceId]);

  useEffect(() => {
    if (tab !== 'supply-requests') return;
    setSupplyRequestsLoading(true);
    setSupplyRequestsError('');
    getSupplyRequests({ spaceId })
      .then((res) => {
        setSupplyRequests(res.data.all || []);
      })
      .catch((err) => {
        setSupplyRequestsError(err?.response?.data?.message || 'Failed to load supply requests');
      })
      .finally(() => setSupplyRequestsLoading(false));
  }, [tab, spaceId]);

  // Load all users lazily when members tab is opened (spaceDetails already loaded on mount)
  useEffect(() => {
    if (tab !== 'members') return;
    if (allUsers.length > 0) return; // already loaded
    api.get('/users')
      .then((res) => setAllUsers(res.data.users || []))
      .catch((err) => console.error('Failed to load users:', err));
  }, [tab]); // eslint-disable-line

  // ── Thread handlers ────────────────────────────────────────────────────────
  const loadMessages = useCallback(
    async (thread) => {
      setActiveThread(thread);
      setEditingMsgId(null);
      try {
        const res = await getMessages(spaceId, thread._id);
        setMessages(res.data.messages || []);
        markThreadRead(spaceId, thread._id).catch(() => {});
      } catch (err) {
        console.error('Failed to load messages:', err);
      }
    },
    [spaceId]
  );

  const handleCreateThread = async (e) => {
    e.preventDefault();
    if (!newSubject.trim()) return;
    const res = await createThread(spaceId, { subject: newSubject.trim(), isPrivate: newThreadPrivate });
    setThreads((prev) => [res.data.thread, ...prev]);
    setNewSubject('');
    setNewThreadPrivate(false);
    setShowNewThread(false);
    loadMessages(res.data.thread);
  };

  const handleDeleteThread = async (e, threadId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this thread? This cannot be undone.')) return;
    try {
      await deleteThread(spaceId, threadId);
      setThreads((prev) => prev.filter((t) => t._id !== threadId));
      if (activeThread?._id === threadId) {
        setActiveThread(null);
        setMessages([]);
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete thread');
    }
  };

  const handleToggleLock = async () => {
    if (!activeThread) return;
    try {
      const res = await toggleThreadLock(spaceId, activeThread._id);
      const updatedLocked = res.data.thread.locked;
      setActiveThread((prev) => ({ ...prev, locked: updatedLocked }));
      setThreads((prev) =>
        prev.map((t) => t._id === activeThread._id ? { ...t, locked: updatedLocked } : t)
      );
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to toggle thread lock');
    }
  };

  // Called by MessageCompose — must return a promise (throw on error)
  const handleMessageSend = async ({ body, files }) => {
    const uploadedAttachments = [];

    for (const file of files || []) {
      const fileRes = await uploadFile(spaceId, file);
      uploadedAttachments.push({
        fileRef: fileRes.data.file._id,
        name: fileRes.data.file.originalName || file.name,
      });
    }

    const res = await sendMessage(spaceId, activeThread._id, { body, attachments: uploadedAttachments });
    setMessages((prev) => [...prev, res.data.message]);
    setThreads((prev) =>
      prev.map((t) =>
        t._id === activeThread._id
          ? { ...t, messageCount: (t.messageCount || 0) + 1, lastMessageAt: new Date() }
          : t
      )
    );
  };

  const handleEditSave = async (msgId) => {
    if (!editBody.trim()) return;
    try {
      await editMessage(spaceId, activeThread._id, msgId, { body: editBody.trim() });
      setMessages((prev) =>
        prev.map((m) =>
          m._id === msgId ? { ...m, body: editBody.trim(), editedAt: new Date() } : m
        )
      );
      setEditingMsgId(null);
    } catch (err) {
      console.error('Edit failed:', err);
    }
  };

  const handleDeleteMsg = async (msgId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await deleteMessage(spaceId, activeThread._id, msgId);
      setMessages((prev) => prev.filter((m) => m._id !== msgId));
      setThreads((prev) =>
        prev.map((t) =>
          t._id === activeThread._id
            ? { ...t, messageCount: Math.max(0, (t.messageCount || 1) - 1) }
            : t
        )
      );
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  // ── Task handlers ──────────────────────────────────────────────────────────

  const syncActiveTask = (updatedTask) => {
    setActiveTask(updatedTask);
    setTasks((prev) => prev.map((t) => (t._id === updatedTask._id ? updatedTask : t)));
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) return;
    try {
      const res = await createTask(spaceId, {
        title: newTaskTitle.trim(),
        priority: newTaskPriority,
      });
      setTasks((prev) => [res.data.task, ...prev]);
      setNewTaskTitle('');
      setNewTaskPriority('medium');
      setShowNewTask(false);
      setActiveTask(res.data.task);
    } catch (err) {
      console.error('Create task failed:', err);
    }
  };

  const handleSelectTask = (task) => {
    setActiveTask(task);
    setEditingTaskTitle(false);
    setEditingTaskDesc(false);
    setShowSpaceFilePicker(false);
  };

  const handleCloseTaskDetail = () => {
    setActiveTask(null);
    setEditingTaskTitle(false);
    setEditingTaskDesc(false);
    setShowSpaceFilePicker(false);
  };

  const handleTaskFieldUpdate = async (field, value) => {
    if (!activeTask) return;
    try {
      const res = await updateTask(spaceId, activeTask._id, { [field]: value });
      syncActiveTask(res.data.task);
    } catch (err) {
      console.error('Task field update failed:', err);
    }
  };

  const handleTaskTitleSave = async () => {
    if (!editTaskTitleVal.trim()) return;
    await handleTaskFieldUpdate('title', editTaskTitleVal.trim());
    setEditingTaskTitle(false);
  };

  const handleTaskDescSave = async () => {
    await handleTaskFieldUpdate('description', editTaskDescVal);
    setEditingTaskDesc(false);
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task? This cannot be undone.')) return;
    try {
      await deleteTask(spaceId, taskId);
      setTasks((prev) => prev.filter((t) => t._id !== taskId));
      if (activeTask?._id === taskId) setActiveTask(null);
    } catch (err) {
      console.error('Delete task failed:', err);
    }
  };

  // ── Subtask handlers ──────────────────────────────────────────────────────
  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtaskTitle.trim() || !activeTask) return;
    try {
      const res = await addSubtask(spaceId, activeTask._id, { title: newSubtaskTitle.trim() });
      syncActiveTask(res.data.task);
      setNewSubtaskTitle('');
    } catch (err) {
      console.error('Add subtask failed:', err);
    }
  };

  const handleToggleSubtask = async (subtaskId, done) => {
    if (!activeTask) return;
    try {
      const res = await updateSubtask(spaceId, activeTask._id, subtaskId, { done });
      syncActiveTask(res.data.task);
    } catch (err) {
      console.error('Toggle subtask failed:', err);
    }
  };

  const handleDeleteSubtask = async (subtaskId) => {
    if (!activeTask) return;
    try {
      const res = await deleteSubtask(spaceId, activeTask._id, subtaskId);
      syncActiveTask(res.data.task);
    } catch (err) {
      console.error('Delete subtask failed:', err);
    }
  };

  // ── Task file attachment handlers ─────────────────────────────────────────
  const handleTaskFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeTask) return;
    try {
      const fileRes = await uploadFile(spaceId, file);
      const fileRef = fileRes.data.file;
      const res = await addTaskAttachment(spaceId, activeTask._id, {
        fileRefId: fileRef._id,
        name: fileRef.originalName || file.name,
      });
      syncActiveTask(res.data.task);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to attach file');
    } finally {
      e.target.value = '';
    }
  };

  const handlePickSpaceFile = async (fileRef) => {
    if (!activeTask) return;
    try {
      const res = await addTaskAttachment(spaceId, activeTask._id, {
        fileRefId: fileRef._id,
        name: fileRef.originalName,
      });
      syncActiveTask(res.data.task);
      setShowSpaceFilePicker(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to attach file');
    }
  };

  const handleRemoveTaskAttachment = async (fileRefId) => {
    if (!activeTask) return;
    try {
      const res = await removeTaskAttachment(spaceId, activeTask._id, fileRefId);
      syncActiveTask(res.data.task);
    } catch (err) {
      console.error('Remove attachment failed:', err);
    }
  };

  const loadSpaceFiles = async () => {
    try {
      const res = await api.get(`/spaces/${spaceId}/files`);
      setSpaceFiles(res.data.files || []);
      setShowSpaceFilePicker(true);
    } catch (err) {
      console.error('Failed to load space files:', err);
    }
  };

  // ── Member handlers ────────────────────────────────────────────────────────
  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addMemberId) return;
    try {
      const res = await api.post(`/spaces/${spaceId}/members`, {
        userId: addMemberId,
        role: addMemberRole,
      });
      syncSpaceState(res.data.space);
      setAddMemberId('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the space?')) return;
    try {
      const res = await api.delete(`/spaces/${spaceId}/members/${userId}`);
      syncSpaceState(res.data.space);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  const existingMemberIds = new Set(
    (spaceDetails?.members || []).map((m) => String(m.user?._id || m.user))
  );
  const availableUsers = allUsers.filter((u) => !existingMemberIds.has(String(u._id)));

  const currentUserRole = spaceDetails?.members?.find(
    (m) => String(m.user?._id || m.user) === String(user?._id)
  )?.role;
  const canSeeSpaceInfo = currentUserRole === 'owner' || currentUserRole === 'admin';

  const handleCopy = (field, value) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // ── Space Info handlers ────────────────────────────────────────────────────
  const handleEditSpaceInfo = () => {
    setEditSpaceName(spaceDetails?.name || '');
    setEditSpaceDescription(spaceDetails?.description || '');
    setEditOrganizationKey(spaceDetails?.organizationKey || '');
    setEditTeamKey(spaceDetails?.teamKey || '');
    setEditParentSpaceId(String(spaceDetails?.parentSpaceId?._id || spaceDetails?.parentSpaceId || ''));
    setEditGovernanceClassification(spaceDetails?.governance?.classification || 'internal');
    setEditGovernanceOwnerType(spaceDetails?.governance?.dataOwnerType || 'space');
    setEditGovernanceOwnerName(spaceDetails?.governance?.dataOwnerName || '');
    setEditRequireAdminApproval(Boolean(spaceDetails?.governance?.requireAdminApprovalForMembership));
    setEditAllowMemberUploads(spaceDetails?.governance?.allowMemberUploads !== false);
    setEditAllowExternalSharing(Boolean(spaceDetails?.governance?.allowExternalSharing));
    setEditAllowDuplicateContainers(spaceDetails?.governance?.allowDuplicateContainers !== false);
    setEditConfidentialAdminIds((spaceDetails?.governance?.confidentialAdminIds || []).map((entry) => String(entry?._id || entry)));
    setEditAllowSupplyRequestWrikePush(Boolean(spaceDetails?.settings?.allowSupplyRequestWrikePush));
    setEditWrikeFolderUrl(spaceDetails?.settings?.wrikeFolderId || spaceDetails?.settings?.wrikeFolderUrl || '');
    setSpaceInfoError('');
    setIsEditingSpaceInfo(true);
  };

  const handleCancelEditSpaceInfo = () => {
    setIsEditingSpaceInfo(false);
    setSpaceInfoError('');
    setWrikeLookupError('');
    setWrikeLookupResolvedId('');
    setWrikeLookupMatches([]);
  };

  const runWrikeFolderLookup = async () => {
    const source = String(editWrikeFolderUrl || '').trim();
    if (!source) {
      setWrikeLookupError('Enter a Wrike URL, folder ID, or folder name first.');
      setWrikeLookupResolvedId('');
      setWrikeLookupMatches([]);
      return;
    }

    setWrikeLookupLoading(true);
    setWrikeLookupError('');
    setWrikeLookupResolvedId('');
    setWrikeLookupMatches([]);

    try {
      const looksLikeUrlOrId = source.includes('/folder/') || source.startsWith('http') || source.startsWith('IE');
      const params = looksLikeUrlOrId
        ? { input: source, limit: 10 }
        : { search: source, limit: 10 };

      const res = await lookupWrikeFolder(params);
      const resolvedId = String(res?.data?.resolvedId || '').trim();
      const matches = Array.isArray(res?.data?.matches) ? res.data.matches : [];

      setWrikeLookupResolvedId(resolvedId);
      setWrikeLookupMatches(matches);

      if (!resolvedId && matches.length === 0) {
        setWrikeLookupError('No matching Wrike folders found. Try a different name or paste another folder URL.');
      }
    } catch (err) {
      setWrikeLookupError(err?.response?.data?.message || 'Failed to lookup Wrike folders.');
    } finally {
      setWrikeLookupLoading(false);
    }
  };

  const applyWrikeFolderId = (folderId) => {
    const normalized = String(folderId || '').trim();
    if (!normalized) return;
    setEditWrikeFolderUrl(normalized);
    setWrikeLookupResolvedId(normalized);
    setWrikeLookupError('');
  };

  const toggleConfidentialAdmin = (userId) => {
    const nextUserId = String(userId);
    setEditConfidentialAdminIds((current) => (
      current.includes(nextUserId)
        ? current.filter((entry) => entry !== nextUserId)
        : [...current, nextUserId]
    ));
  };

  const handleSaveSpaceInfo = async (e) => {
    e.preventDefault();
    if (!editSpaceName.trim()) {
      setSpaceInfoError('Space name cannot be empty');
      return;
    }
    setUpdatingSpaceInfo(true);
    setSpaceInfoError('');
    try {
      const wrikeFolderInput = editWrikeFolderUrl.trim();
      const res = await api.patch(`/spaces/${spaceId}`, {
        name: editSpaceName.trim(),
        description: editSpaceDescription.trim(),
        organizationKey: editOrganizationKey.trim(),
        teamKey: editTeamKey.trim(),
        parentSpaceId: editParentSpaceId || null,
        governance: {
          classification: editGovernanceClassification,
          dataOwnerType: editGovernanceOwnerType,
          dataOwnerName: editGovernanceOwnerName.trim(),
          requireAdminApprovalForMembership: editRequireAdminApproval,
          allowMemberUploads: editAllowMemberUploads,
          allowExternalSharing: editAllowExternalSharing,
          allowDuplicateContainers: editAllowDuplicateContainers,
          confidentialAdminIds: editConfidentialAdminIds,
        },
        settings: {
          ...(spaceDetails?.settings || {}),
          allowSupplyRequestWrikePush: editAllowSupplyRequestWrikePush,
          wrikeFolderUrl: buildWrikeFolderHref(wrikeFolderInput),
          wrikeFolderId: wrikeFolderInput,
        },
      });
      syncSpaceState(res.data.space);
      upsertSpace(res.data.space);
      await fetchSpaces();
      setIsEditingSpaceInfo(false);
      setWrikeLookupError('');
      setWrikeLookupResolvedId('');
      setWrikeLookupMatches([]);
    } catch (err) {
      setSpaceInfoError(err?.response?.data?.message || 'Failed to update space info');
    } finally {
      setUpdatingSpaceInfo(false);
    }
  };

  return (
    <div className="space-page">
      {/* ── Tab header ─────────────────────────────────────────────────────── */}
      <div className="space-tabs-bar">
        <span className="space-title-label">{activeSpace?.name || 'Space'}</span>
        <div className="space-tab-buttons">
          {['threads', 'tasks', 'members', 'info'].map((t) => (
            <button
              key={t}
              className={`space-tab-btn ${tab === t ? 'active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t === 'threads' ? '💬 Threads' : t === 'tasks' ? '✓ Tasks' : t === 'members' ? '👥 Members' : 'ℹ️ Info'}
            </button>
          ))}
        </div>
      </div>

      {/* ── THREADS tab ────────────────────────────────────────────────────── */}
      {tab === 'threads' && (
        <div className="space-tab-content threads-layout">
          {/* Thread list */}
          <div className="thread-panel">
            <div className="thread-panel-header">
              <span>Threads</span>
              <button className="btn-icon" onClick={() => setShowNewThread((v) => !v)}>+</button>
            </div>

            {showNewThread && (
              <form onSubmit={handleCreateThread} className="new-thread-form">
                <input
                  autoFocus
                  className="thread-input"
                  placeholder="Thread subject..."
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                />
                <label className="thread-private-toggle" title="Only visible to participants">
                  <input
                    type="checkbox"
                    checked={newThreadPrivate}
                    onChange={(e) => setNewThreadPrivate(e.target.checked)}
                  />
                  🔒
                </label>
                <button type="submit" className="btn-sm btn-primary">Start</button>
              </form>
            )}

            {threadsLoading ? (
              <p className="panel-muted">Loading...</p>
            ) : threads.length === 0 ? (
              <p className="panel-muted">No threads yet.</p>
            ) : (
              <ul className="thread-list">
                {threads.map((t) => {
                  const isThreadCreator = String(t.createdBy?._id || t.createdBy) === String(user?._id);
                  const canManage = isThreadCreator || canSeeSpaceInfo;
                  return (
                    <li
                      key={t._id}
                      className={`thread-item ${activeThread?._id === t._id ? 'active' : ''}`}
                      onClick={() => loadMessages(t)}
                    >
                      <div className="thread-subject">
                        {t.isPrivate && '🔒 '}
                        {t.locked && <span className="thread-locked-icon" title="Thread is closed">🔒</span>}
                        {t.subject}
                      </div>
                      <div className="thread-meta">{t.messageCount} msg · {t.type}</div>
                      {canManage && (
                        <button
                          className="thread-delete-btn"
                          title="Delete thread"
                          onClick={(e) => handleDeleteThread(e, t._id)}
                        >✕</button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Message panel */}
          <div className="message-panel">
            {!activeThread ? (
              <div className="message-empty">Select a thread to view messages</div>
            ) : (
              <>
                <div className="message-panel-header">
                  <h2>{activeThread.subject}</h2>
                  {(String(activeThread.createdBy?._id || activeThread.createdBy) === String(user?._id) || canSeeSpaceInfo) && (
                    <button
                      className={`thread-lock-btn ${activeThread.locked ? 'locked' : ''}`}
                      title={activeThread.locked ? 'Reopen thread' : 'Close thread (lock comments)'}
                      onClick={handleToggleLock}
                    >
                      {activeThread.locked ? '🔓 Reopen' : '🔒 Close'}
                    </button>
                  )}
                </div>
                <div className="message-list">
                  {messages.length === 0 ? (
                    <p className="panel-muted">No messages yet. Be the first.</p>
                  ) : (
                    messages.map((msg) => {
                      const isOwn = String(msg.sender?._id) === String(user?._id);
                      return (
                        <div key={msg._id} className={`message-bubble ${isOwn ? 'own' : ''}`}>
                          <div className="msg-header">
                            <span className="msg-sender">
                              {msg.mailboxId ? `📫 ${msg.mailboxId.name}` : msg.sender?.name}
                            </span>
                            <span className="msg-time">
                              {new Date(msg.createdAt).toLocaleString()}
                              {msg.editedAt && <em className="msg-edited"> (edited)</em>}
                            </span>
                            {isOwn && editingMsgId !== msg._id && (
                              <div className="msg-actions">
                                <button
                                  className="msg-action-btn"
                                  title="Edit"
                                  onClick={() => { setEditingMsgId(msg._id); setEditBody(msg.body); }}
                                >✎</button>
                                <button
                                  className="msg-action-btn msg-action-delete"
                                  title="Delete"
                                  onClick={() => handleDeleteMsg(msg._id)}
                                >✕</button>
                              </div>
                            )}
                          </div>
                          {editingMsgId === msg._id ? (
                            <div className="msg-edit-form">
                              <textarea
                                className="msg-edit-input"
                                value={editBody}
                                onChange={(e) => setEditBody(e.target.value)}
                                autoFocus
                                rows={3}
                              />
                              <div className="msg-edit-actions">
                                <button className="btn-sm btn-primary" onClick={() => handleEditSave(msg._id)}>Save</button>
                                <button className="btn-sm btn-ghost" onClick={() => setEditingMsgId(null)}>Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="msg-body">{msg.body}</div>
                              {Array.isArray(msg.attachments) && msg.attachments.length > 0 && (
                                <div className="space-attachments">
                                  {msg.attachments.map((attachment, idx) => {
                                    const fileId = attachment.fileRef?._id || attachment.fileRef;
                                    const fileName = attachment.name || attachment.fileRef?.originalName || 'Attachment';
                                    return (
                                      <div className="space-attachment-item" key={fileId || `${msg._id}-att-${idx}`}>
                                        <button
                                          type="button"
                                          className="space-attachment-link"
                                          onClick={() => openFile(fileId).catch(() => alert('Unable to open attachment'))}
                                          title="Open in new tab"
                                        >
                                          📎 {fileName}
                                        </button>
                                        <button
                                          type="button"
                                          className="space-attachment-download"
                                          onClick={() => downloadFile(fileId, fileName)}
                                          title="Download"
                                        >
                                          ⬇
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
                {activeThread.locked ? (
                  <div className="thread-locked-notice">
                    🔒 This thread is closed. No new messages can be added.
                    {(String(activeThread.createdBy?._id || activeThread.createdBy) === String(user?._id) || canSeeSpaceInfo) && (
                      <button className="thread-reopen-btn" onClick={handleToggleLock}>Reopen</button>
                    )}
                  </div>
                ) : (
                  <MessageCompose
                    onSend={handleMessageSend}
                    members={spaceMembers}
                    disabled={!activeThread}
                  />
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── TASKS tab ──────────────────────────────────────────────────────── */}
      {tab === 'tasks' && (
        <div className="space-tab-content tasks-layout">
          {/* Kanban area */}
          <div className={`kanban-area ${activeTask ? 'with-panel' : ''}`}>
            <div className="tasks-toolbar">
              <button className="btn btn-primary btn-sm" onClick={() => setShowNewTask((v) => !v)}>
                + New Task
              </button>
            </div>

            {showNewTask && (
              <form onSubmit={handleCreateTask} className="new-task-form">
                <input
                  autoFocus
                  className="task-title-input"
                  placeholder="Task title..."
                  value={newTaskTitle}
                  onChange={(e) => setNewTaskTitle(e.target.value)}
                />
                <select
                  className="task-priority-select"
                  value={newTaskPriority}
                  onChange={(e) => setNewTaskPriority(e.target.value)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <button type="submit" className="btn-sm btn-primary" disabled={!newTaskTitle.trim()}>Add</button>
                <button type="button" className="btn-sm btn-ghost" onClick={() => setShowNewTask(false)}>Cancel</button>
              </form>
            )}

            {tasksLoading ? (
              <p className="panel-muted" style={{ padding: '20px 24px' }}>Loading tasks...</p>
            ) : (
              <div className="kanban-board">
                {STATUS_COLS.map((status) => (
                  <div key={status} className="kanban-col">
                    <div className="kanban-col-header">
                      <span className={`kanban-dot dot-${status}`} />
                      <span className="kanban-col-label">{STATUS_LABELS[status]}</span>
                      <span className="kanban-col-count">{tasks.filter((t) => t.status === status).length}</span>
                    </div>
                    <div className="kanban-cards">
                      {tasks.filter((t) => t.status === status).length === 0 && (
                        <p className="kanban-empty">No tasks</p>
                      )}
                      {tasks.filter((t) => t.status === status).map((task) => (
                        <div
                          key={task._id}
                          className={`kanban-card ${activeTask?._id === task._id ? 'selected' : ''}`}
                          onClick={() => handleSelectTask(task)}
                        >
                          <div className="kanban-card-top">
                            <span
                              className="kanban-priority-dot"
                              style={{ background: PRIORITY_COLORS[task.priority] }}
                              title={task.priority + ' priority'}
                            />
                            <span className="kanban-card-title">{task.title}</span>
                          </div>
                          {((task.subtasks?.length > 0) || task.dueDate || task.assignedTo) && (
                            <div className="kanban-card-meta">
                              {task.subtasks?.length > 0 && (
                                <span className="kanban-meta-chip">
                                  ☑ {task.subtasks.filter((s) => s.done).length}/{task.subtasks.length}
                                </span>
                              )}
                              {task.dueDate && (
                                <span className="kanban-meta-chip">
                                  📅 {new Date(task.dueDate).toLocaleDateString()}
                                </span>
                              )}
                              {task.assignedTo && (
                                <span className="kanban-meta-chip">👤 {task.assignedTo.name}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Task Detail Panel ────────────────────────────────────────── */}
          {activeTask && (
            <div className="task-detail-panel">
              <div className="task-detail-header">
                <span className="task-detail-id">Task</span>
                <button className="task-detail-close" onClick={handleCloseTaskDetail} title="Close">✕</button>
              </div>

              {/* Title */}
              <div className="task-detail-title-row">
                {editingTaskTitle ? (
                  <div className="task-title-edit">
                    <input
                      autoFocus
                      className="form-input"
                      value={editTaskTitleVal}
                      onChange={(e) => setEditTaskTitleVal(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTaskTitleSave(); if (e.key === 'Escape') setEditingTaskTitle(false); }}
                    />
                    <div className="task-title-edit-actions">
                      <button className="btn btn-primary btn-sm" onClick={handleTaskTitleSave}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingTaskTitle(false)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <h2
                    className="task-detail-title"
                    onClick={() => { setEditTaskTitleVal(activeTask.title); setEditingTaskTitle(true); }}
                    title="Click to edit"
                  >
                    {activeTask.title}
                  </h2>
                )}
              </div>

              {/* Status / Priority / Assignee / Due Date */}
              <div className="task-detail-fields">
                <div className="task-field-row">
                  <span className="task-field-label">Status</span>
                  <select
                    className="task-field-select"
                    value={activeTask.status}
                    onChange={(e) => handleTaskFieldUpdate('status', e.target.value)}
                  >
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="done">Done</option>
                  </select>
                </div>
                <div className="task-field-row">
                  <span className="task-field-label">Priority</span>
                  <select
                    className="task-field-select"
                    value={activeTask.priority}
                    onChange={(e) => handleTaskFieldUpdate('priority', e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="task-field-row">
                  <span className="task-field-label">Assigned to</span>
                  <select
                    className="task-field-select"
                    value={activeTask.assignedTo?._id || activeTask.assignedTo || ''}
                    onChange={(e) => handleTaskFieldUpdate('assignedTo', e.target.value || null)}
                  >
                    <option value="">Unassigned</option>
                    {(spaceDetails?.members || []).map((m) => (
                      <option key={m.user?._id} value={m.user?._id}>{m.user?.name}</option>
                    ))}
                  </select>
                </div>
                <div className="task-field-row">
                  <span className="task-field-label">Due date</span>
                  <input
                    type="date"
                    className="task-field-input"
                    value={activeTask.dueDate ? new Date(activeTask.dueDate).toISOString().split('T')[0] : ''}
                    onChange={(e) => handleTaskFieldUpdate('dueDate', e.target.value || null)}
                  />
                </div>
                <div className="task-field-row">
                  <span className="task-field-label">Project</span>
                  <select
                    className="task-field-select"
                    value={activeTask.projectId?._id || activeTask.projectId || ''}
                    onChange={(e) => handleTaskFieldUpdate('projectId', e.target.value || null)}
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p._id} value={p._id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Description */}
              <div className="task-detail-section">
                <div className="task-section-label">Description</div>
                {editingTaskDesc ? (
                  <div className="task-desc-edit">
                    <textarea
                      autoFocus
                      className="form-textarea"
                      value={editTaskDescVal}
                      onChange={(e) => setEditTaskDescVal(e.target.value)}
                      rows={4}
                      placeholder="Add a description..."
                    />
                    <div className="task-title-edit-actions">
                      <button className="btn btn-primary btn-sm" onClick={handleTaskDescSave}>Save</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditingTaskDesc(false)}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`task-desc-display ${!activeTask.description ? 'task-desc-empty' : ''}`}
                    onClick={() => { setEditTaskDescVal(activeTask.description || ''); setEditingTaskDesc(true); }}
                    title="Click to edit"
                  >
                    {activeTask.description || 'Add a description…'}
                  </div>
                )}
              </div>

              {/* Subtasks */}
              <div className="task-detail-section">
                <div className="task-section-label">
                  Subtasks
                  {activeTask.subtasks?.length > 0 && (
                    <span className="task-section-count">
                      {activeTask.subtasks.filter((s) => s.done).length}/{activeTask.subtasks.length}
                    </span>
                  )}
                </div>
                <ul className="subtask-list">
                  {(activeTask.subtasks || []).map((st) => (
                    <li key={st._id} className={`subtask-item ${st.done ? 'done' : ''}`}>
                      <input
                        type="checkbox"
                        className="subtask-checkbox"
                        checked={st.done}
                        onChange={(e) => handleToggleSubtask(st._id, e.target.checked)}
                      />
                      <span className="subtask-title">{st.title}</span>
                      <button
                        className="subtask-delete"
                        onClick={() => handleDeleteSubtask(st._id)}
                        title="Remove subtask"
                      >✕</button>
                    </li>
                  ))}
                </ul>
                <form onSubmit={handleAddSubtask} className="subtask-add-form">
                  <input
                    className="subtask-add-input"
                    placeholder="Add subtask…"
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                  />
                  <button type="submit" className="btn btn-ghost btn-sm" disabled={!newSubtaskTitle.trim()}>+</button>
                </form>
              </div>

              {/* Attachments */}
              <div className="task-detail-section">
                <div className="task-section-label">Attachments</div>
                {(activeTask.attachments || []).length > 0 && (
                  <ul className="task-attachment-list">
                    {activeTask.attachments.map((att) => {
                      const fileId = att.fileRef?._id || att.fileRef;
                      return (
                        <li key={String(fileId)} className="task-attachment-item">
                          <button
                            className="task-attachment-name"
                            onClick={() => openFile(fileId).catch(() => alert('Unable to open file'))}
                            title="Open file"
                          >
                            📎 {att.name}
                          </button>
                          <button
                            className="task-attachment-download"
                            onClick={() => downloadFile(fileId, att.name)}
                            title="Download"
                          >⬇</button>
                          <button
                            className="task-attachment-remove"
                            onClick={() => handleRemoveTaskAttachment(String(fileId))}
                            title="Remove attachment"
                          >✕</button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                <div className="task-attachment-actions">
                  <label className="btn btn-ghost btn-sm task-upload-btn">
                    ⬆ Upload file
                    <input type="file" style={{ display: 'none' }} onChange={handleTaskFileUpload} />
                  </label>
                  <button className="btn btn-ghost btn-sm" onClick={loadSpaceFiles}>
                    📁 Pick from space
                  </button>
                </div>

                {/* Space file picker */}
                {showSpaceFilePicker && (
                  <div className="space-file-picker">
                    <div className="space-file-picker-header">
                      <span>Space Files</span>
                      <button className="btn-icon" onClick={() => setShowSpaceFilePicker(false)}>✕</button>
                    </div>
                    {spaceFiles.length === 0 ? (
                      <p className="panel-muted">No files in this space.</p>
                    ) : (
                      <ul className="space-file-picker-list">
                        {spaceFiles
                          .filter((f) => !(activeTask.attachments || []).some(
                            (a) => String(a.fileRef?._id || a.fileRef) === String(f._id)
                          ))
                          .map((f) => (
                            <li
                              key={f._id}
                              className="space-file-picker-item"
                              onClick={() => handlePickSpaceFile(f)}
                            >
                              📎 {f.originalName}
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Danger zone */}
              <div className="task-detail-footer">
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteTask(activeTask._id)}
                >
                  Delete Task
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SUPPLY REQUESTS tab ─────────────────────────────────────────── */}
      {tab === 'supply-requests' && (
        <div className="space-tab-content">
          <div className="tasks-toolbar" style={{ justifyContent: 'space-between' }}>
            <div>
              <h2 className="members-heading" style={{ marginBottom: 4 }}>Supply Requests</h2>
              <p className="panel-muted">Requests created in this space and tracked locally in ComSapp.</p>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {getWrikeFolderValue(spaceDetails) && (
                <a
                  className="btn btn-ghost btn-sm"
                  href={buildWrikeFolderHref(getWrikeFolderValue(spaceDetails))}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open Wrike Folder
                </a>
              )}
              <button className="btn btn-primary btn-sm" onClick={() => window.open(`/wrike/supply-requests?spaceId=${encodeURIComponent(spaceId)}`, '_self')}>
                Open Full Supply Requests
              </button>
            </div>
          </div>

          {supplyRequestsLoading ? (
            <p className="panel-muted" style={{ padding: '20px 24px' }}>Loading supply requests...</p>
          ) : supplyRequestsError ? (
            <p className="panel-muted" style={{ padding: '20px 24px' }}>{supplyRequestsError}</p>
          ) : supplyRequests.length === 0 ? (
            <p className="panel-muted" style={{ padding: '20px 24px' }}>No supply requests yet.</p>
          ) : (
            <ul className="thread-list">
              {supplyRequests.map((request) => (
                <li key={request.id} className="thread-item">
                  <div className="thread-subject">{request.title}</div>
                  <div className="thread-meta">
                    {request.visibleStatus || request.standardStatus || 'new'} · {request.syncState || 'local_only'}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── MEMBERS tab ────────────────────────────────────────────────────── */}
      {tab === 'members' && (
        <div className="space-tab-content members-layout">
          <div className="members-section">
            <h2 className="members-heading">Space Members</h2>
            {!spaceDetails ? (
              <p className="panel-muted">Loading...</p>
            ) : (
              <ul className="members-list">
                {(spaceDetails?.members || []).map((m, idx) => (
                  <li key={m.user?._id ? String(m.user._id) : `member-${idx}`} className="member-item">
                    <div className="member-avatar">{(m.user?.name || '?')[0].toUpperCase()}</div>
                    <div className="member-info">
                      <span className="member-name">{m.user?.name || 'Unknown'}</span>
                      <span className="member-email">{m.user?.email}</span>
                    </div>
                    <span className={`member-role-badge role-${m.role}`}>{m.role}</span>
                    {m.role !== 'owner' && (
                      <button
                        className="btn-icon-danger"
                        title="Remove member"
                        onClick={() => handleRemoveMember(m.user?._id)}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {spaceDetails && availableUsers.length > 0 && (
            <div className="add-member-section">
              <h3 className="add-member-heading">Add Member</h3>
              <form onSubmit={handleAddMember} className="add-member-row">
                <select
                  className="form-select"
                  value={addMemberId}
                  onChange={(e) => setAddMemberId(e.target.value)}
                >
                  <option value="">Select a user...</option>
                  {availableUsers.map((u) => (
                    <option key={u._id} value={u._id}>{u.name} — {u.email}</option>
                  ))}
                </select>
                <select
                  className="form-select form-select-sm"
                  value={addMemberRole}
                  onChange={(e) => setAddMemberRole(e.target.value)}
                >
                  <option value="viewer">Viewer</option>
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" className="btn btn-primary" disabled={!addMemberId}>Add</button>
              </form>
            </div>
          )}

          {spaceDetails && availableUsers.length === 0 && (spaceDetails?.members?.length || 0) > 0 && (
            <p className="panel-muted" style={{ marginTop: 12 }}>All users are already members.</p>
          )}
        </div>
      )}

      {/* ── INFO tab ──────────────────────────────────────────────────────── */}
      {tab === 'info' && (
        <div className="space-tab-content info-layout">
          {!canSeeSpaceInfo ? (
            <p className="panel-muted" style={{ padding: '20px 24px' }}>You don't have permission to manage space info. Only space owners and admins can edit this.</p>
          ) : (
            <>
              {/* ── Space Name and Description Editing ────────────────────── */}
              <div className="space-info-edit-section">
                <h2 className="space-info-section-title">Space Settings</h2>
                {isEditingSpaceInfo ? (
                  <form onSubmit={handleSaveSpaceInfo} className="space-info-form">
                    {spaceInfoError && (
                      <div className="form-error-message">{spaceInfoError}</div>
                    )}
                    <div className="form-group">
                      <label htmlFor="spaceName" className="form-label">Space Name</label>
                      <input
                        id="spaceName"
                        type="text"
                        className="form-input"
                        value={editSpaceName}
                        onChange={(e) => setEditSpaceName(e.target.value)}
                        placeholder="Enter space name"
                        autoFocus
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="spaceDescription" className="form-label">Description (optional)</label>
                      <textarea
                        id="spaceDescription"
                        className="form-textarea"
                        value={editSpaceDescription}
                        onChange={(e) => setEditSpaceDescription(e.target.value)}
                        placeholder="Enter space description"
                        rows={4}
                      />
                    </div>
                    <div className="space-info-grid">
                      <div className="form-group">
                        <label htmlFor="organizationKey" className="form-label">Business / Organization</label>
                        <input
                          id="organizationKey"
                          type="text"
                          className="form-input"
                          value={editOrganizationKey}
                          onChange={(e) => setEditOrganizationKey(e.target.value)}
                          placeholder="Example: Goondocks"
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="teamKey" className="form-label">Team</label>
                        <input
                          id="teamKey"
                          type="text"
                          className="form-input"
                          value={editTeamKey}
                          onChange={(e) => setEditTeamKey(e.target.value)}
                          placeholder="Example: Accounting"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="parentSpaceId" className="form-label">Parent Space</label>
                      <select
                        id="parentSpaceId"
                        className="form-select"
                        value={editParentSpaceId}
                        onChange={(e) => setEditParentSpaceId(e.target.value)}
                      >
                        <option value="">No parent space</option>
                        {spaces
                          .filter((space) => String(space._id) !== String(spaceId))
                          .map((space) => (
                            <option key={space._id} value={space._id}>
                              {space.name}
                            </option>
                          ))}
                      </select>
                    </div>
                    <div className="space-governance-card">
                      <h3 className="space-governance-title">Governance</h3>
                      <div className="space-info-grid">
                        <div className="form-group">
                          <label htmlFor="governanceClassification" className="form-label">Classification</label>
                          <select
                            id="governanceClassification"
                            className="form-select"
                            value={editGovernanceClassification}
                            onChange={(e) => setEditGovernanceClassification(e.target.value)}
                          >
                            <option value="internal">Internal</option>
                            <option value="confidential">Confidential</option>
                            <option value="restricted">Restricted</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label htmlFor="governanceOwnerType" className="form-label">Data Owner Type</label>
                          <select
                            id="governanceOwnerType"
                            className="form-select"
                            value={editGovernanceOwnerType}
                            onChange={(e) => setEditGovernanceOwnerType(e.target.value)}
                          >
                            <option value="space">Space</option>
                            <option value="business">Business</option>
                            <option value="user">User</option>
                            <option value="shared">Shared</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-group">
                        <label htmlFor="governanceOwnerName" className="form-label">Data Owner Name</label>
                        <input
                          id="governanceOwnerName"
                          type="text"
                          className="form-input"
                          value={editGovernanceOwnerName}
                          onChange={(e) => setEditGovernanceOwnerName(e.target.value)}
                          placeholder="Example: Goondocks Accounting"
                        />
                      </div>
                      <div className="governance-toggle-list">
                        <label className="form-label governance-toggle-item">
                          <input
                            type="checkbox"
                            checked={editRequireAdminApproval}
                            onChange={(e) => setEditRequireAdminApproval(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          Require admin approval for new members
                        </label>
                        <label className="form-label governance-toggle-item">
                          <input
                            type="checkbox"
                            checked={editAllowMemberUploads}
                            onChange={(e) => setEditAllowMemberUploads(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          Allow members to upload files directly
                        </label>
                        <label className="form-label governance-toggle-item">
                          <input
                            type="checkbox"
                            checked={editAllowExternalSharing}
                            onChange={(e) => setEditAllowExternalSharing(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          Allow external sharing
                        </label>
                        <label className="form-label governance-toggle-item">
                          <input
                            type="checkbox"
                            checked={editAllowDuplicateContainers}
                            onChange={(e) => setEditAllowDuplicateContainers(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          Allow duplicate containers with the same business purpose
                        </label>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Confidential Admins</label>
                        <div className="governance-admin-list">
                          {(spaceDetails?.members || []).map((member) => {
                            const memberId = String(member.user?._id || member.user);
                            return (
                              <label key={memberId} className="governance-admin-item">
                                <input
                                  type="checkbox"
                                  checked={editConfidentialAdminIds.includes(memberId)}
                                  onChange={() => toggleConfidentialAdmin(memberId)}
                                />
                                <span>{member.user?.name || 'Member'}</span>
                                <span className="governance-admin-role">{member.role}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label" htmlFor="allowSupplyRequestWrikePush">
                        <input
                          id="allowSupplyRequestWrikePush"
                          type="checkbox"
                          checked={editAllowSupplyRequestWrikePush}
                          onChange={(e) => setEditAllowSupplyRequestWrikePush(e.target.checked)}
                          style={{ marginRight: 8 }}
                        />
                        Allow supply requests from this space to push to Wrike
                      </label>
                    </div>
                    <div className="form-group">
                      <label htmlFor="wrikeFolderUrl" className="form-label">Wrike Folder Link or ID</label>
                      <input
                        id="wrikeFolderUrl"
                        type="text"
                        className="form-input"
                        value={editWrikeFolderUrl}
                        onChange={(e) => setEditWrikeFolderUrl(e.target.value)}
                        placeholder="Paste the Wrike folder URL or folder ID"
                      />
                      <div className="wrike-lookup-actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={runWrikeFolderLookup}
                          disabled={wrikeLookupLoading}
                        >
                          {wrikeLookupLoading ? 'Looking up...' : 'Lookup Folder ID'}
                        </button>
                        {wrikeLookupResolvedId && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => applyWrikeFolderId(wrikeLookupResolvedId)}
                          >
                            Use {wrikeLookupResolvedId}
                          </button>
                        )}
                      </div>
                      {wrikeLookupError && <div className="wrike-lookup-error">{wrikeLookupError}</div>}
                      {wrikeLookupMatches.length > 0 && (
                        <div className="wrike-lookup-results">
                          {wrikeLookupMatches.map((match) => (
                            <div key={match.id} className="wrike-lookup-result-item">
                              <div className="wrike-lookup-result-main">
                                <strong>{match.title || 'Untitled Folder'}</strong>
                                <span>{match.id}</span>
                              </div>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => applyWrikeFolderId(match.id)}
                              >
                                Use ID
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary" disabled={updatingSpaceInfo}>
                        {updatingSpaceInfo ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={handleCancelEditSpaceInfo}
                        disabled={updatingSpaceInfo}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="space-info-display">
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Space Name</span>
                      <span className="space-info-display-value">{spaceDetails?.name || 'Unnamed'}</span>
                    </div>
                    {spaceDetails?.description && (
                      <div className="space-info-display-item">
                        <span className="space-info-display-label">Description</span>
                        <span className="space-info-display-value">{spaceDetails.description}</span>
                      </div>
                    )}
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Wrike Push</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.settings?.allowSupplyRequestWrikePush ? 'Allowed' : 'Disabled'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Business / Team</span>
                      <span className="space-info-display-value">
                        {(spaceDetails?.organizationKey || 'Independent')} / {(spaceDetails?.teamKey || 'General')}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Parent Space</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.parentSpaceId?.name || 'None'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Classification</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.governance?.classification || 'internal'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Data Ownership</span>
                      <span className="space-info-display-value">
                        {(spaceDetails?.governance?.dataOwnerName || 'Unnamed owner')} ({spaceDetails?.governance?.dataOwnerType || 'space'})
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Membership Policy</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.governance?.requireAdminApprovalForMembership ? 'Admin approval required' : 'Standard membership flow'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Upload Policy</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.governance?.allowMemberUploads !== false ? 'Members can upload' : 'Admin-managed uploads only'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">External Sharing</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.governance?.allowExternalSharing ? 'Allowed' : 'Blocked'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Duplicate Containers</span>
                      <span className="space-info-display-value">
                        {spaceDetails?.governance?.allowDuplicateContainers !== false ? 'Allowed' : 'Blocked'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Confidential Admins</span>
                      <span className="space-info-display-value">
                        {(spaceDetails?.members || [])
                          .filter((member) => (spaceDetails?.governance?.confidentialAdminIds || []).map((entry) => String(entry?._id || entry)).includes(String(member.user?._id || member.user)))
                          .map((member) => member.user?.name || 'Member')
                          .join(', ') || 'None assigned'}
                      </span>
                    </div>
                    <div className="space-info-display-item">
                      <span className="space-info-display-label">Wrike Folder</span>
                      {getWrikeFolderValue(spaceDetails) ? (
                        <a
                          className="wrike-inline-link"
                          href={buildWrikeFolderHref(getWrikeFolderValue(spaceDetails))}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {getWrikeFolderValue(spaceDetails)}
                        </a>
                      ) : (
                        <span className="space-info-display-value">Not set</span>
                      )}
                    </div>
                    <button className="btn btn-primary btn-sm" onClick={handleEditSpaceInfo}>
                      Edit Settings
                    </button>
                  </div>
                )}
              </div>

              {/* ── Space Connection Info ────────────────────────────────── */}
              <div className="space-connection-info-section">
                <h3 className="space-info-section-title">Space Connection Info</h3>
                <p className="space-info-hint">
                  Use either value to link this space from RS-Education, CIP, or KOYoga connector settings.
                </p>
                <div className="space-info-row">
                  <span className="space-info-label">Slug</span>
                  <code className="space-info-value">{spaceDetails?.slug}</code>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy('slug', spaceDetails?.slug)}
                    title="Copy slug"
                  >
                    {copiedField === 'slug' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                <div className="space-info-row">
                  <span className="space-info-label">Space ID</span>
                  <code className="space-info-value">{spaceId}</code>
                  <button
                    className="copy-btn"
                    onClick={() => handleCopy('id', spaceId)}
                    title="Copy space ID"
                  >
                    {copiedField === 'id' ? '✓ Copied' : 'Copy'}
                  </button>
                </div>
                {(spaceDetails?.connectedApps || []).length > 0 && (
                  <div className="space-info-connected">
                    <span className="space-info-label">Connected apps</span>
                    <span className="space-info-apps">
                      {spaceDetails.connectedApps.map((a) => (
                        <span key={a.appId} className="space-info-app-badge">{a.appId}</span>
                      ))}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

